// Ported from eb-ui packages/attendee/trending/src/app.tsx (the inner content only —
// the surrounding <SiteStructure> header/footer chrome is now provided by the page,
// via consumer-header.global-header + legacy-infra.site-footer). Behaviour preserved:
//   - timeframe state + client re-fetch cache (flag-off path);
//   - search-engine-referrer-gated analytics;
//   - the `launchConsolidateAndExpandTrendingSearches` flag fork.
// Dropped: initializeRealUserMonitoring (prod-only telemetry, not gating — see report).

import React, { useEffect, useRef, useState } from 'react';
import { gettext } from '../i18n/gettext';
import {
    httpRefererWasSearchEngine,
    trackTrendingEvent,
} from '../lib/analytics';
import { Timeframe, TrendingAppProps, TrendingSearch } from '../types';
import { fetchTrendingSearchesClient } from '../api/trendingSearches';
import { formatPageTitle, formatTrendingSearchPageTitle } from '../utils';
import SearchResults from './SearchResults';
import TimeframeNavList from './TimeframeNavList';
import TrendingSearches from './TrendingSearches';

const TrendingApp = (props: TrendingAppProps) => {
    const {
        featureFlags,
        locationStringForUrl,
        displayLocation,
        httpReferer,
        localeSubdirectory,
        shouldNoFollowLinks,
        trendingSearches,
        degradedTrending = false,
    } = props;

    const [activeTimeframe, setActiveTimeframe] = useState<Timeframe>(props.activeTimeframe);
    const [trendingSearchResults, setTrendingSearchResults] = useState<
        Partial<Record<Timeframe, TrendingSearch[]>>
    >(
        // Seed the initial timeframe ONLY from a genuine fetch. A degraded empty list
        // (transient 502/503/504 swallowed) must not register as "already fetched" —
        // empty arrays are truthy, and so would pin the empty state for the session.
        degradedTrending ? {} : { [props.activeTimeframe]: props.trendingSearches },
    );
    // In-flight/timeframe bookkeeping so a re-click can't issue a duplicate BFF fetch.
    // `activeTimeframe` alone can't gate this: on the FIRST click of a tab it's still
    // the OLD timeframe, so a double-click (or a fast re-render) would fire twice.
    const inFlightRef = useRef<Partial<Record<Timeframe, boolean>>>({});

    const pageTitle = featureFlags.launchConsolidateAndExpandTrendingSearches
        ? formatTrendingSearchPageTitle(displayLocation)
        : formatPageTitle(displayLocation, activeTimeframe);

    // The page's <title> is set server-side from the INITIAL timeframe. When the visitor
    // switches timeframe client-side the title depends on it (formatPageTitle), so keep the
    // document title in sync — matching eb-ui, where the title derived from the stateful
    // activeTimeframe. (OG/Twitter tags stay SSR-only: they're crawler-facing and a client
    // tab switch never re-runs for a crawler.)
    useEffect(() => {
        document.title = pageTitle;
    }, [pageTitle]);

    const handleSearchResultClick = (searchTerm: string) => {
        if (httpRefererWasSearchEngine(httpReferer)) {
            trackTrendingEvent({
                category: 'trending-searches',
                action: 'ViewTrendingSearch',
                label: searchTerm,
            });
        }
    };

    const handleTimeframeSelect = (timeframe: Timeframe, location: string) => {
        if (httpRefererWasSearchEngine(httpReferer)) {
            // Track date range switch
            trackTrendingEvent({
                category: 'trending-searches',
                action: 'AdjustDateRange',
                label: timeframe,
            });
        }

        // Already fetched this timeframe? Just switch to it.
        if (trendingSearchResults[timeframe]) {
            setActiveTimeframe(timeframe);
            return;
        }

        // Already fetching it? Drop the duplicate click (the first request will
        // populate state and switch the tab when it lands).
        if (inFlightRef.current[timeframe]) {
            return;
        }

        // Re-fetch this timeframe from the app's OWN BFF (`<basePath>/api/trending`), not
        // Core. The BFF owns the upstream fetch and serves fixtures under TRENDING_FIXTURES,
        // so this works identically on localhost and in prod — no offline dead-tab branch.
        inFlightRef.current[timeframe] = true;
        fetchTrendingSearchesClient(location, timeframe)
            .then((response) => {
                // A degraded upstream response (transient 502/503/504 swallowed -> an
                // EMPTY list) must NOT be cached in the "already fetched" map — an empty
                // array is truthy, so it would otherwise pin the empty state for the
                // whole session even after Core recovers. Store the result only when it
                // is a genuine successful fetch (or a genuinely-empty location, which is
                // indistinguishable but harmless to re-fetch).
                if (response._degraded === true) {
                    return; // skip caching; the tab falls back to loading/current state
                }
                setTrendingSearchResults((current) => ({
                    ...current,
                    [timeframe]: response.trending.trending_searches,
                }));
                setActiveTimeframe(timeframe);
            })
            .catch((error) => {
                // BFF re-fetch failed (upstream error) — keep the current timeframe.
                console.error('[trending] failed to re-fetch timeframe', timeframe, error);
            })
            .finally(() => {
                inFlightRef.current[timeframe] = false;
            });
    };

    return (
        <>
            <h1 className="eds-show-up-md eds-text-hm eds-l-pad-top-5">{pageTitle}</h1>
            <h2 className="eds-text-bl eds-l-pad-bot-5 eds-l-sm-mar-top-5 eds-l-md-mar-top-5">
                {displayLocation === 'Online'
                    ? gettext('Browse around and see what events are happening online')
                    : gettext('Browse around and see what events are happening in your city.')}
            </h2>

            {!featureFlags.launchConsolidateAndExpandTrendingSearches && (
                <>
                    <TimeframeNavList
                        activeTimeframe={activeTimeframe}
                        location={locationStringForUrl}
                        onSelect={handleTimeframeSelect}
                    />

                    <SearchResults
                        searches={trendingSearchResults[activeTimeframe] || []}
                        location={locationStringForUrl}
                        displayLocation={displayLocation}
                        activeTimeframe={activeTimeframe}
                        shouldNoFollowLinks={shouldNoFollowLinks}
                        onSearchClick={handleSearchResultClick}
                        localeSubdirectory={localeSubdirectory}
                    />
                </>
            )}

            {featureFlags.launchConsolidateAndExpandTrendingSearches && (
                <TrendingSearches
                    searches={trendingSearches}
                    location={locationStringForUrl}
                    displayLocation={displayLocation}
                    shouldNoFollowLinks={shouldNoFollowLinks}
                    onSearchClick={handleSearchResultClick}
                    localeSubdirectory={localeSubdirectory}
                />
            )}
        </>
    );
};

export default TrendingApp;
